import React from 'react';

import {
  ForbiddenError,
  TokenExpirationError,
  UnauthorizedError,
} from 'utils/errors';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { ServicePageLayoutComponent } from 'design/servicePageLayout';
import { LinkControlComponent } from 'design/linkControl';
import { BackControlComponent } from 'design/backControl';
import { ORIGIN_SITE_URL } from 'utils/constants';
import { LogoComponent } from 'design/logo';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { ContentLayoutComponent } from 'design/contentLayout';
import { useServices } from 'hooks/useServices';
import { EmailSupportLink } from 'containers/EmailSupportLink';
import { TelegramSupportLink } from 'containers/TelegramSupportLink';

type Props = {
  error: Error;
  restore(): void;
};

export const AppErrorFallback = (props: Props) => {
  const { error, restore } = props;

  const { signOut } = useServices();

  const intl = useIntlDictionary();

  const dispatch = useAppDispatcher();

  const { appFallback: appFallbackIntl } = intl;

  const descriptionNode = React.useMemo(() => {
    if (error instanceof TokenExpirationError) {
      return <TextComponent>{intl.tokenExpirationError}</TextComponent>;
    }

    return appFallbackIntl.criticalDetailsText(
      <EmailSupportLink />,
      <TelegramSupportLink />
    );
  }, [appFallbackIntl, error, intl.tokenExpirationError]);

  React.useLayoutEffect(() => {
    if (error instanceof UnauthorizedError) {
      dispatch({ name: 'AuthProcess__AuthFailed' });

      restore();
    }
  }, [dispatch, error, restore]);

  React.useEffect(() => {
    if (error instanceof ForbiddenError) {
      signOut().then(() => {
        dispatch({
          name: 'AppErrorFallback__SignedOut',
        });

        restore();
      });
    }
  }, [dispatch, error, restore, signOut]);

  if (error instanceof ForbiddenError) {
    return null;
  }

  return (
    <ServicePageLayoutComponent
      sidebarContent={
        <LinkControlComponent to={ORIGIN_SITE_URL}>
          <TextComponent size="l">
            <LogoComponent />
          </TextComponent>
        </LinkControlComponent>
      }
      bodyContent={
        <ContentLayoutComponent
          title={
            <TextComponent size="l" family="futura">
              {appFallbackIntl.titleText}
            </TextComponent>
          }
          body={descriptionNode}
          backAction={<BackControlComponent onPress={restore} />}
        />
      }
    />
  );
};
